import React from "react";
import type { SingleValue } from "react-select";
import t from "../../i18n";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { ApiValue } from "./formHelpers";
import { getDefaultValue, multipleValue } from "./formHelpers";
import type { Project } from "../../api/useProjects";
import { uniq } from "diagnostics-lib/array";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../api/useCategories";

// ProjectSite (unique with full option)
export const useProjectSiteInput = (selectedProjects: Project[]) => {
  const [isDirty, setIsDirty] = React.useState(false);
  const projectSites = useCategories("PROJECT_SITE");
  const createProjectSite = useCreateCategory("PROJECT_SITE");
  const updateProjectSite = useUpdateCategory("PROJECT_SITE");
  const deleteProjectSite = useDeleteCategory("PROJECT_SITE");

  const siteFromSelection = uniq(
    selectedProjects.map(project => ({ id: project.siteId, name: project.siteId ?? "" })),
  );

  const defaultValue = getDefaultValue<ApiValue>(siteFromSelection, "id", projectSite => ({
    label: projectSite.name,
    value: String(projectSite.id),
  }));

  const projectSiteOptions = React.useMemo(
    () => [
      { id: emptyOption.value, name: emptyOption.label },
      ...projectSites.map(c => ({ id: c, name: c })),
    ],
    [projectSites],
  );

  const [defaultTypeOption, setDefaultTypeOption] = React.useState(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) return null;
    return defaultValue;
  });

  const [selectedProjectSites, setSelectedProjectSite] =
    React.useState<SingleValue<BaseOption>>(defaultTypeOption);

  const handleProjectSiteChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedProjectSite(option);
  }, []);

  React.useEffect(() => {
    if (selectedProjectSites?.value === defaultTypeOption?.value) {
      setIsDirty(false);
    } else {
      setIsDirty(true);
    }
  }, [selectedProjectSites, defaultTypeOption]);

  const resetValue = React.useCallback(() => {
    setSelectedProjectSite(defaultTypeOption);
    setIsDirty(false);
  }, [defaultTypeOption]);

  const handleProjectSiteCreated = React.useCallback(
    async (newProjectSite: string) => {
      await createProjectSite(newProjectSite);
      setIsDirty(true);

      setSelectedProjectSite({
        label: newProjectSite,
        value: newProjectSite,
      });
    },
    [createProjectSite],
  );

  const handleProjectSiteEdited = React.useCallback(
    async (option: BaseOption, label: string) => {
      if (!option.value) return;
      await updateProjectSite(option.value, label);
      if (selectedProjectSites?.value === option.value) {
        setSelectedProjectSite({ ...option, label });
      }
    },
    [updateProjectSite, setSelectedProjectSite, selectedProjectSites],
  );

  const handleProjectSiteDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (!option.value) return;
      await deleteProjectSite(option.value);
      if (selectedProjectSites?.value === option.value) {
        setDefaultTypeOption(null);
        setSelectedProjectSite(null);
      }
    },
    [deleteProjectSite, selectedProjectSites],
  );

  const placeholder = React.useMemo(
    () =>
      defaultValue
        ? defaultValue.value === multipleValue.value
          ? t("projects.modal.multiple_value")
          : t("form.no_value")
        : t("projects.modal.create_or_edit.input.site"),
    [defaultValue],
  );

  return {
    placeholder,
    projectSiteOptions,
    selectedProjectSites,
    setSelectedProjectSite,
    handleProjectSiteChanged,
    handleProjectSiteCreated,
    handleProjectSiteEdited,
    handleProjectSiteDeleted,
    isDirty,
    resetValue,
  };
};
